import Filter from "@/components/Filter";
import {IColumnMetadata} from "@/features/application/api/applicationModels";
import React, {FC, useEffect, useState} from "react";
import {Button, Checkbox, Icon, Popup} from "semantic-ui-react";
import dataFormatUtil from "@/util/dataFormatUtil";
import {IDataTableFilter, ITextFilterOptions} from "./DataTableModels";

interface IDataTableHeaderFilterString {
    //the unique values from the column of data
    filterOptions: ITextFilterOptions;
    //filters can also be set from outside the column filter itself, like clicking on an employee
    //in another component and then seeing just that employee in the grid.
    //The "applied" filters are controlled by a parent component.  When this component needs to update
    //the applied filters it does it via the parent by calling setFilters which is passed in as a prop here
    appliedFilters?: IDataTableFilter;
    column: IColumnMetadata;
    setFilters: (
        filterColumnMetricCode: string,
        selectedValues: IDataTableFilter
    ) => void;
}

const DataTableHeaderFilterString: FC<IDataTableHeaderFilterString> = ({
    filterOptions,
    appliedFilters,
    column,
    setFilters
}) => {
    //selected, but not necessarily applied filters
    const [selectedFilters, setSelectedFilters] = useState<IDataTableFilter>(
        new Set()
    );

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [searchWithinFilterValue, setSearchWithinFilterValue] = useState("");

    useEffect(() => {
        if (appliedFilters) {
            const initialFilters: IDataTableFilter = new Set(appliedFilters);

            setSelectedFilters(initialFilters);
        } else {
            setSelectedFilters(new Set());
        }
    }, [appliedFilters]);

    function handleFilterChange(filter: string) {
        setSearchWithinFilterValue(filter);
    }

    const displayFilterOptions = (
        filterChoices: ITextFilterOptions,
        searchWithinFilterValue: string,
        column: IColumnMetadata
    ) => {
        const filterContent: JSX.Element[] = [];

        //turn into an array so it can be filtered
        let filterChoicesAsArray = [...filterChoices];
        if (searchWithinFilterValue > "") {
            filterChoicesAsArray = filterChoicesAsArray.filter((x) =>
                x
                    ?.toString()
                    .toLowerCase()
                    .includes(searchWithinFilterValue.toLowerCase())
            );
        }

        filterChoicesAsArray.forEach((filterValue, index) => {
            let value: string | null | undefined;
            if (filterValue === null) {
                value = null;
            } else if (filterValue === undefined) {
                value = undefined;
            } else {
                value = filterValue.toString();
            }

            filterContent.push(
                <React.Fragment key={index}>
                    <Checkbox
                        key={value}
                        name={filterValue?.toString()}
                        label={dataFormatUtil.dataTableDisplayValue(value, column)}
                        style={{marginBottom: "1em"}}
                        checked={selectedFilters.has(value)}
                        onClick={() => {
                            toggleSelection(value);
                        }}
                    />
                    <br />
                </React.Fragment>
            );
        });

        const checkboxes = (
            <>
                {filterChoices.size > 8 && (
                    <>
                        <Filter
                            filter={searchWithinFilterValue}
                            changeFilter={handleFilterChange}
                            filterPlaceholder={"Search"}
                            compact
                        ></Filter>
                        <br />
                        <br />
                    </>
                )}
                {/* hide the Select All option if they've typed something in the search box 
                    since it will select the hidden elements as well
                */}
                {searchWithinFilterValue === "" && (
                    <>
                        <Checkbox
                            key={column.key + "-selectAll"}
                            name="(Select All)"
                            label="(Select All)"
                            //all options are checked
                            checked={selectedFilters.size === filterChoices.size}
                            onClick={() => {
                                toggleSelectAll(
                                    selectedFilters.size === filterChoices.size
                                );
                            }}
                        ></Checkbox>
                        <br />
                        <br />
                    </>
                )}

                {filterContent}
            </>
        );

        return checkboxes;
    };

    const toggleSelectAll = (currentlyChecked: boolean) => {
        if (currentlyChecked) {
            //deselect everything
            setSelectedFilters(new Set());
        } else {
            //select everything
            setSelectedFilters(new Set(filterOptions));
        }
    };

    const toggleSelection = (
        filterValue: string | boolean | number | undefined | null
    ) => {
        if (selectedFilters.has(filterValue)) {
            //extra work to create a new Set object with the value removed for the state value
            //candidate for using immutable.js
            const newFilters = new Set(selectedFilters);
            newFilters.delete(filterValue);

            setSelectedFilters(newFilters);
        } else {
            const newFilters = new Set(selectedFilters);
            newFilters.add(filterValue);

            setSelectedFilters(newFilters);
        }
    };

    return (
        <Popup
            key={column.key}
            trigger={
                <Icon
                    name="filter"
                    circular={true}
                    className="filterIcon"
                    size="small"
                    inverted={appliedFilters ? true : false}
                ></Icon>
            }
            open={isFilterOpen}
            onOpen={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onOpen stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(true);
            }}
            onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                // Stop propagation on onClose stops propagation (sorting the column) when the popup icon is clicked
                event.stopPropagation();
                setIsFilterOpen(false);
                //reset selected filter to the applied filters
                setSelectedFilters(new Set(appliedFilters));
            }}
            onClick={(e: any) => {
                // Stop propagation on onClick stops propagation (sorting the column) when the popup body is clicked
                e.stopPropagation();
            }}
            //this is needed when adding a text box
            //popperDependencies={[filterOptionsSomething.length]}
            position={"top center"}
            openOnTriggerMouseEnter={false}
            openOnTriggerClick={true}
            closeOnTriggerClick={true}
            closeOnDocumentClick={true}
            closeOnEscape={true}
            closeOnTriggerMouseLeave={false}
            style={{maxHeight: "400px"}}
        >
            Filter by:
            <br />
            <br />
            <div style={{maxHeight: "300px", overflow: "auto"}}>
                {displayFilterOptions(
                    filterOptions,
                    searchWithinFilterValue,
                    column
                )}
            </div>
            <Button
                className="btn btn-blue-solid"
                onClick={() => {
                    setFilters(column.key, selectedFilters);
                    setIsFilterOpen(false);
                }}
                style={{marginTop: "5px"}}
            >
                Apply
            </Button>
            <Button
                className="btn btn-blue"
                onClick={() => {
                    setIsFilterOpen(false);
                    //reset selected filter to the applied filters
                    setSelectedFilters(new Set(appliedFilters));
                }}
            >
                Cancel
            </Button>
        </Popup>
    );
};

export default DataTableHeaderFilterString;
